import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { Cookie, Shield } from 'lucide-react';

const ANIMATION_DELAY_MS = 1500;
const ANIMATION_TRANSITION_MS = 50;

export const CookieConsent = () => {
  const { theme } = useTheme();
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    // Check if user has already made a choice
    const consentGiven = localStorage.getItem('atlas_cookie_consent');
    if (!consentGiven) {
      // Delay showing the banner for better UX
      const timer = setTimeout(() => {
        setIsVisible(true);
        setTimeout(() => setIsAnimating(true), ANIMATION_TRANSITION_MS);
      }, ANIMATION_DELAY_MS);
      return () => clearTimeout(timer);
    }
  }, []);

  // Listen for manage cookie preferences event from footer
  useEffect(() => {
    const handleManageCookies = () => {
      setIsVisible(true);
      setTimeout(() => setIsAnimating(true), ANIMATION_TRANSITION_MS);
    };

    window.addEventListener('manageCookiePreferences', handleManageCookies);
    return () => {
      window.removeEventListener('manageCookiePreferences', handleManageCookies);
    };
  }, []);

  const handleAccept = () => {
    localStorage.setItem('atlas_cookie_consent', 'accepted');
    localStorage.setItem('atlas_cookie_consent_date', new Date().toISOString());
    closeBanner();
  };

  const handleDecline = () => {
    localStorage.setItem('atlas_cookie_consent', 'declined');
    localStorage.setItem('atlas_cookie_consent_date', new Date().toISOString());
    closeBanner();
  };

  const closeBanner = () => {
    setIsAnimating(false);
    setTimeout(() => setIsVisible(false), 300);
  };

  if (!isVisible) return null;

  return (
    <div 
      data-testid="cookie-consent-banner"
      className={`fixed bottom-0 left-0 right-0 z-50 p-4 transition-all duration-300 ease-out ${
        isAnimating ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
      }`}
    >
      <div 
        className={`max-w-6xl mx-auto rounded-2xl shadow-2xl border backdrop-blur-xl ${
          theme === 'dark' 
            ? 'bg-[#12131F]/95 border-[#464B8B]/30' 
            : 'bg-white/95 border-gray-200'
        }`}
      >
        <div className="p-6 md:p-8">
          <div className="flex flex-col lg:flex-row lg:items-center gap-6">
            {/* Icon and Text */}
            <div className="flex-1 flex items-start gap-4">
              <div 
                className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, #464B8B 0%, #00C8EB 100%)' }}
              >
                <Cookie className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 
                  className={`text-lg font-semibold mb-2 ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}
                >
                  We Value Your Privacy
                </h3>
                <p 
                  className={`text-sm leading-relaxed ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                  }`}
                >
                  We use cookies to enhance your browsing experience, analyze site traffic, and personalize content. 
                  In accordance with the{' '}
                  <span className="font-medium" style={{ color: '#00C8EB' }}>
                    Privacy and Data Protection Act of Tanzania
                  </span>
                  , we respect your right to privacy.{' '}
                  <Link 
                    to="/cookie-policy" 
                    className="underline hover:no-underline font-medium transition-colors"
                    style={{ color: '#464B8B' }}
                    data-testid="cookie-policy-link"
                  >
                    Learn more about our Cookie Policy
                  </Link>
                </p>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 lg:flex-shrink-0">
              <button
                onClick={handleDecline}
                data-testid="cookie-decline-btn"
                className={`px-6 py-3 rounded-xl font-medium text-sm transition-all duration-200 border ${
                  theme === 'dark'
                    ? 'border-[#464B8B]/50 text-gray-300 hover:bg-[#464B8B]/20 hover:border-[#464B8B]'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-100 hover:border-gray-400'
                }`}
              >
                Decline Optional
              </button>
              <button
                onClick={handleAccept}
                data-testid="cookie-accept-btn"
                className="px-6 py-3 rounded-xl font-medium text-sm text-white transition-all duration-200 hover:shadow-lg hover:scale-105"
                style={{ background: 'linear-gradient(135deg, #464B8B 0%, #00C8EB 100%)' }}
              >
                Accept All Cookies
              </button>
            </div>
          </div>

          {/* Additional Info */}
          <div 
            className={`mt-4 pt-4 border-t flex items-center gap-2 text-xs ${
              theme === 'dark' ? 'border-[#464B8B]/20 text-gray-400' : 'border-gray-100 text-gray-500'
            }`}
          >
            <Shield className="w-4 h-4" style={{ color: '#00C8EB' }} />
            <span>
              Your data is protected under Tanzanian law. View our{' '}
              <Link 
                to="/privacy-policy" 
                className="underline hover:no-underline"
                style={{ color: '#464B8B' }}
              >
                Privacy Policy
              </Link>
              {' '}and{' '}
              <Link 
                to="/terms-of-service" 
                className="underline hover:no-underline"
                style={{ color: '#464B8B' }}
              >
                Terms of Service
              </Link>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
